import axios, { AxiosError, AxiosRequestConfig } from "axios";
import config from "../Constants/Config";

// Models
import {RegisterUserRequest, RegisterUserResponse} from "../Models/DTOs/Account";
import {LoginDTO} from "../Models/DTOs/LoginDTO";
import {AuthUser} from "../Models/DTOs/AuthUser";

// Helpers
import {axiosPost} from "../Helpers/Api";

const BACKEND_URL_WEB = process.env.REACT_APP_WEB_API_URL ?? "/";
const BACKEND_URL_AUTH = process.env.REACT_APP_AUTH_API_URL ?? "/";

export async function RegisterUser(
    payload: RegisterUserRequest,
    abortController: AbortController
): Promise<RegisterUserResponse | AxiosError> {
    try {
        const url = BACKEND_URL_WEB + "v1/users/register";

        // Use your existing helper for consistency (handles JSON headers, etc.)
        const res = await axiosPost<RegisterUserResponse>(url, payload, abortController);

        return res.data;
    } catch (err: unknown) {
        if (axios.isAxiosError(err)) return err;
        return new AxiosError("Non-Axios error", "ERR_UNKNOWN");
    }
}

// -----------------------------------------------------------------------------
// POST /v1/login  (auth-service)
// Body: { username, password }
// -----------------------------------------------------------------------------
type AnyObj = Record<string, any>;
export async function LoginUser(
    username: string,
    password: string,
    abortController: AbortController
): Promise<LoginDTO | AxiosError> {
    try {
        const url = BACKEND_URL_AUTH + "v1/login";
        const res = await axiosPost<AnyObj>(url, { username, password }, abortController);
        const body = res.data ?? {};
        const data = body.data ?? body;
        return data.tokens;
    } catch (err: unknown) {
        if (axios.isAxiosError(err)) return err;
        return new AxiosError("Non-Axios error", "ERR_UNKNOWN");
    }
}

export async function GetAuthUser(
    accessToken: string,
    abortController: AbortController
): Promise<AuthUser | AxiosError> {
    try {
        const res = await axios.get<{ data?: AuthUser }>(
            BACKEND_URL_AUTH + "v1/verify",
            {
                signal: abortController.signal,
                timeout: 15000,
                headers: { Authorization: "Bearer " + accessToken },
            }
        );

        // Your auth service uses { data: ... }
        const payload = (res.data?.data ?? res.data) as AuthUser;
        return payload;
    } catch (err: unknown) {
        return axios.isAxiosError(err) ? err : new AxiosError("ERR_UNKNOWN");
    }
}

export async function ChangePassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
    accessToken: string,
    abortController: AbortController
): Promise<true | AxiosError> {
    try {
        await axios.put(
            `${BACKEND_URL_WEB}v1/user/password`,
            {
                current_password: currentPassword,
                new_password: newPassword,
                confirm_password: confirmPassword,
            },
            {
                signal: abortController.signal,
                headers: {
                    Authorization: "Bearer " + accessToken,
                },
            } as AxiosRequestConfig
        );

        return true as const;
    } catch (err) {
        return err as AxiosError;
    }
}

export async function ChangeEmail(
    newEmail: string,
    access_token: string,
    abortController: AbortController
): Promise<true | AxiosError> {
    try {
        await axios.put(
            BACKEND_URL_WEB + "v1/user/email",
            { email: newEmail },
            {
                signal: abortController.signal,
                headers: {
                    Authorization: "Bearer " + access_token,
                },
            } as AxiosRequestConfig
        );

        // literal true, not boolean
        return true as const;
    } catch (err) {
        const error = err as AxiosError;
        console.log("ChangeEmail Error", error);
        return error;
    }
}


export async function ForgotPassword(email: string) {
    const encodedEmail = encodeURIComponent(email);

    const responce = await axios
        .post(process.env.REACT_APP_API_URL + config.account + "ForgotPassword", {
            email: encodedEmail,
            url: window.location.origin
        } as AxiosRequestConfig)
        .then((result) => {
            return result.data as boolean;
        })
        .catch((error: AxiosError) => {
            console.log("ForgotPassword Error");
            return error;
        });

    return responce;
}

export async function ResetPassword(
    email: string,
    code: string,
    newPassword: string,
    abortController: AbortController
) {
    const responce = await axios
        .post(process.env.REACT_APP_API_URL + config.account + "ResetPassword", { email: email, code: code, password: newPassword }, {
            signal: abortController.signal,
        } as AxiosRequestConfig)
        .then((result) => {
            return result.data as boolean;
        })
        .catch((error: AxiosError) => {
            console.log("ResetPassword Error");
            return error;
        });

    return responce;
}

export async function ChangeMarketingPreferences(
    prefs: { allowCommercialEmails?: boolean; allowDailyNewsletter?: boolean },
    accessToken: string,
    abortController: AbortController
): Promise<true | AxiosError> {
    try {
        await axios.put(
            BACKEND_URL_WEB + "v1/user/marketing-preferences",
            {
                allow_commercial_emails: prefs.allowCommercialEmails,
                allow_daily_newsletter: prefs.allowDailyNewsletter,
            },
            {
                signal: abortController.signal,
                headers: {
                    Authorization: "Bearer " + accessToken,
                },
            } as AxiosRequestConfig
        );

        return true as const;
    } catch (err) {
        return err as AxiosError;
    }
}
